import React from 'react';

// One card per content item, styled like the Scenes screen's cards (ScenePanel). The Art Therapy
// list names its buttons after these too.
export const RESOURCE_CARDS = {
  pdf: {
    icon: '📄',
    label: 'Worksheet',
    action: 'Open PDF',
    card: 'bg-rose-50',
    iconColor: 'text-rose-500',
    button: 'bg-white border-2 border-rose-500 text-rose-600 hover:bg-rose-50',
  },
  audio: {
    icon: '🎧',
    label: 'Audio',
    action: 'Play Audio',
    card: 'bg-green-50',
    iconColor: 'text-green-500',
    button: 'bg-green-500 border-2 border-green-500 text-white hover:bg-green-600',
  },
  video: {
    icon: '🎥',
    label: 'Video',
    action: 'Watch Video',
    card: 'bg-blue-50',
    iconColor: 'text-blue-500',
    button: 'bg-blue-500 border-2 border-blue-500 text-white hover:bg-blue-600',
  },
  link: {
    icon: '🎮',
    label: 'Game',
    action: 'Play Game',
    card: 'bg-amber-50',
    iconColor: 'text-amber-500',
    button: 'bg-amber-500 border-2 border-amber-500 text-white hover:bg-amber-600',
  },
  quiz: {
    icon: '❓',
    label: 'Quiz',
    action: 'Start Quiz',
    card: 'bg-violet-50',
    iconColor: 'text-violet-500',
    button: 'bg-violet-600 border-2 border-violet-600 text-white hover:bg-violet-700',
  },
  image: {
    icon: '🖼️',
    label: 'Picture',
    action: 'View Picture',
    card: 'bg-sky-50',
    iconColor: 'text-sky-500',
    button: 'bg-white border-2 border-sky-500 text-sky-700 hover:bg-sky-50',
  },
  text: {
    icon: '📝',
    label: 'Reading',
    action: 'Read',
    card: 'bg-slate-50',
    iconColor: 'text-slate-500',
    button: 'bg-white border-2 border-slate-400 text-slate-700 hover:bg-slate-100',
  },
  task: {
    icon: '✅',
    label: 'Task',
    action: 'Open Task',
    card: 'bg-teal-50',
    iconColor: 'text-teal-500',
    button: 'bg-white border-2 border-teal-500 text-teal-700 hover:bg-teal-50',
  },
};

/**
 * A course chapter (lesson / story / scene saved with Add New Content): its
 * title, ISF coins, short description and a card per content item.
 * `onOpenItem(item)` opens one.
 */
export default function CourseChapterPanel({ chapter, onOpenItem }) {
  // Two items of the same type are told apart by their own titles.
  const typeCounts = chapter.items.reduce((counts, item) => ({ ...counts, [item.type]: (counts[item.type] || 0) + 1 }), {});

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-[26px] font-extrabold text-slate-900">{chapter.title}</h2>
        {chapter.coins > 0 && (
          <span className="shrink-0 flex items-center gap-1.5 bg-amber-100 text-amber-700 font-extrabold rounded-full px-4 py-1.5 text-sm">
            🪙 {chapter.coins} ISF Coins
          </span>
        )}
      </div>

      {chapter.description && (
        <p className="bg-blue-50 text-blue-800 font-semibold text-[15px] rounded-xl px-4 py-3.5 mb-6">{chapter.description}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {chapter.items.map((item) => {
          const resource = RESOURCE_CARDS[item.type] || RESOURCE_CARDS.text;
          const name = typeCounts[item.type] > 1 ? item.title : resource.label;
          return (
            <div key={item.id} className={`rounded-2xl p-5 flex flex-col items-center text-center gap-3 ${resource.card}`}>
              <span className={`text-5xl ${resource.iconColor}`}>{resource.icon}</span>
              <span className="font-extrabold text-slate-800 leading-tight break-words max-w-full">
                {chapter.title}
                <br />
                {name}
              </span>
              <button
                onClick={() => onOpenItem(item)}
                aria-label={`${resource.action}: ${chapter.title} ${name}`}
                className={`mt-auto w-full rounded-xl px-2 py-2.5 text-sm font-extrabold whitespace-nowrap transition-colors ${resource.button}`}
              >
                {resource.action}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
